import { Info, Send, X } from "lucide-react";
import { useState } from "react";
import { GoogleRecipientInput } from "@/components/pto-entries/GoogleRecipientInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { PtoEntry } from "@/hooks/use-pto-entries";
import {
  usePtoEntryCalendarEventStatus,
  useSendPtoEntryCalendarEvent,
} from "@/hooks/use-pto-entries";
import { formatDate, formatTimestampDate } from "@/lib/format";

interface PtoEntrySendCalendarEventDialogProps {
  entry: PtoEntry | null;
  onOpenChange: (open: boolean) => void;
}

function dateRangeLabel(entry: PtoEntry): string {
  return entry.startDate === entry.endDate
    ? formatDate(entry.startDate)
    : `${formatDate(entry.startDate)} – ${formatDate(entry.endDate)}`;
}

/**
 * Send-invite dialog — mirrors PtoEntryEditDialog's shell. The form is kept
 * inline (not split into a separate *Form component like PtoEntryForm)
 * since it's a single array-of-strings control, not a multi-field
 * react-hook-form entity — see the EPIC-013 build spec.
 */
export function PtoEntrySendCalendarEventDialog({
  entry,
  onOpenChange,
}: PtoEntrySendCalendarEventDialogProps) {
  return (
    <Dialog open={entry !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send calendar invite</DialogTitle>
          <DialogDescription>
            {entry
              ? `Send a Google Calendar invite covering ${dateRangeLabel(entry)} to one or more recipients.`
              : null}
          </DialogDescription>
        </DialogHeader>
        {entry && (
          <SendCalendarEventBody
            key={entry.id}
            entryId={entry.id}
            onDone={() => onOpenChange(false)}
            onCancel={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SendCalendarEventBody({
  entryId,
  onDone,
  onCancel,
}: {
  entryId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [recipients, setRecipients] = useState<string[]>([]);
  const status = usePtoEntryCalendarEventStatus(entryId, { enabled: true });
  const sendEvent = useSendPtoEntryCalendarEvent(entryId);
  const lastSent = status.data ?? null;

  return (
    <div className="space-y-4">
      {lastSent && (
        <div className="flex items-start gap-2 rounded-md border border-border/50 bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <p>
            An invite for this entry was already sent on {formatTimestampDate(lastSent.at)} to{" "}
            {lastSent.recipients.join(", ")}. Sending again creates a new, separate invite.
          </p>
        </div>
      )}
      <GoogleRecipientInput
        value={recipients}
        onChange={setRecipients}
        disabled={sendEvent.isPending}
      />
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          <X className="h-4 w-4 mr-1" />
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={recipients.length === 0 || sendEvent.isPending}
          onClick={() => sendEvent.mutate(recipients, { onSuccess: onDone })}
        >
          <Send className="h-4 w-4 mr-1" />
          {sendEvent.isPending ? "Sending…" : "Send invite"}
        </Button>
      </div>
    </div>
  );
}
